import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import {
    Modal,
    AlertDialog,
    Button,
    Table,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
    Input,
    SimpleSelect,
    RadioGroup,
    RadioGroupItem,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from '@/components/shared/ui';
import { useAuth } from '@/contexts/AuthContext';
import { Organization } from '../../../types/admin';
import {
    useUpdateOrganization,
    useCreateOrganization,
    useCountries,
    useOrganizationTypes,
    useOrganizationDetail,
    useCountryCallingCodes,
    useOrganizationTitleList,
} from '@/hooks/useOrganization';
import type { UpdateOrganizationBodyDto, CreateOrganizationRequestDto } from '../../../types/api';
import { uploadFileViaPresignedUrl } from '@/services/fileService';

/** API가 boolean 외에 문자열/숫자로 반환할 수 있음 */
function toBooleanIsPartner(v: unknown): boolean {
    if (v === true || v === 1 || v === 'true' || v === '1') return true;
    return false;
}

interface OrganizationInfoFormData {
    title: string;
    logo: string;
    countryId: number | null;
    organizationTypeId: number | null;
    businessRegistrationNumber: string;
    isPartner: boolean;
    managerName: string;
    department: string;
    countryCallingCodeId: number | null;
    phoneNumber: string;
    email: string;
}

interface OrganizationInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave?: (org: Organization | null) => void;
    /** null이면 신규 등록 모드 */
    organization: Organization | null;
    /** 수정 모달 오픈 시 선택 탭 (등록 모드에서는 무시되고 기관 정보 탭) */
    initialTab?: 'organization' | 'contact';
}

export default function OrganizationInfoModal({
    isOpen,
    onClose,
    onSave,
    organization,
    initialTab = 'organization',
}: OrganizationInfoModalProps): React.ReactElement {
    const [formData, setFormData] = useState<OrganizationInfoFormData>({
        title: '',
        logo: '',
        countryId: null,
        organizationTypeId: null,
        businessRegistrationNumber: '',
        isPartner: false,
        managerName: '',
        department: '',
        countryCallingCodeId: null,
        phoneNumber: '',
        email: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: '' });
    const [logoUploading, setLogoUploading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isCustomName, setIsCustomName] = useState(false);
    const [orgEditTab, setOrgEditTab] = useState<'organization' | 'contact'>('organization');
    const [searchText, setSearchText] = useState('');
    /** 모달이 방금 열렸을 때만 폼 초기화 (리렌더 시 입력 덮어쓰기 방지) */
    const didOpenRef = useRef(false);
    /** detailData 기준으로 한 번이라도 폼을 채웠는지 (나중에 도착한 detailData 반영용) */
    const syncedFromDetailRef = useRef(false);

    const { t } = useTranslation('admin');
    const { hasRole } = useAuth();
    const isAdmin = hasRole?.('admin') ?? false;
    const isCreateMode = organization == null;
    const updateMutation = useUpdateOrganization();
    const createMutation = useCreateOrganization();
    const { data: countriesData } = useCountries();
    const { data: organizationTypesData } = useOrganizationTypes();
    const { data: countryCallingCodesData } = useCountryCallingCodes();
    const organizationId = organization?.id ? Number(organization.id) : 0;
    const { data: detailData, isLoading: isDetailLoading } = useOrganizationDetail(organizationId, {
        enabled: !!organizationId && isOpen && !isCreateMode,
    });

    const { data: titleListData } = useOrganizationTitleList(
        { searchText: searchText.trim() },
        { enabled: isOpen && isCreateMode && searchText.trim().length > 0 }
    );

    const apiCountries = countriesData?.countryList || [];
    const apiOrganizationTypes = organizationTypesData?.organizationTypeList || [];
    const apiCountryCallingCodes = countryCallingCodesData?.countryCallingCodeList || [];
    const suggestedOrganizations = titleListData?.organizations || [];

    const countryCallingCodeOptions = apiCountryCallingCodes.filter((code, index, list) => {
        const normalizedCode = code.code.replace(/^\+/, '');
        const firstPreferredIndex = list.findIndex((item) => {
            const itemNormalizedCode = item.code.replace(/^\+/, '');
            return itemNormalizedCode === normalizedCode && item.code.startsWith('+');
        });
        if (firstPreferredIndex >= 0) return index === firstPreferredIndex;
        return list.findIndex((item) => item.code.replace(/^\+/, '') === normalizedCode) === index;
    });

    const getKoreaCountry = () =>
        apiCountries.find((c) => c.code?.toUpperCase() === 'KR') ??
        apiCountries.find((c) => c.title === '대한민국') ??
        null;

    const getDefaultCallingCodeId = (countryId: number | null): number | null => {
        if (!countryId) return null;
        const country = apiCountries.find((c) => c.countryId === countryId);
        if (country?.code?.toUpperCase() === 'KR' || country?.title === '대한민국') {
            return countryCallingCodeOptions.find((ccc) => ccc.code === '+82')?.countryCallingCodeId ?? null;
        }
        if (country?.defaultCountryCallingCodeId) return country.defaultCountryCallingCodeId;
        return null;
    };

    useEffect(() => {
        if (isOpen) setOrgEditTab(isCreateMode ? 'organization' : initialTab);
    }, [isOpen, initialTab, isCreateMode]);

    useEffect(() => {
        if (!isOpen) {
            didOpenRef.current = false;
            syncedFromDetailRef.current = false;
            setSearchText('');
            setShowSuggestions(false);
            return;
        }
        const justOpened = !didOpenRef.current;
        didOpenRef.current = true;

        if (isCreateMode) {
            if (justOpened) {
                const defaultCountryId = getKoreaCountry()?.countryId ?? null;
                const defaultCallingCodeId = getDefaultCallingCodeId(defaultCountryId);
                setFormData({
                    title: '',
                    logo: '',
                    countryId: defaultCountryId,
                    organizationTypeId: null,
                    businessRegistrationNumber: '',
                    isPartner: false,
                    managerName: '',
                    department: '',
                    countryCallingCodeId: defaultCallingCodeId,
                    phoneNumber: '',
                    email: '',
                });
                setIsCustomName(false);
            }
            return;
        }
        if (!organization) return;

        if (detailData) {
            if (justOpened || !syncedFromDetailRef.current) {
                const title = detailData.title || '';
                setFormData((prev) => ({
                    ...prev,
                    title,
                    logo: detailData.logoImageUrl || '',
                    countryId: detailData.country?.id ?? null,
                    organizationTypeId: detailData.organizationType?.id ?? null,
                    businessRegistrationNumber: detailData.businessRegistrationNumber || '',
                    isPartner: toBooleanIsPartner(detailData.isPartner),
                    managerName: detailData.managerName || '',
                    department: detailData.department || '',
                    countryCallingCodeId: detailData.countryCallingCode?.id ?? null,
                    phoneNumber: detailData.phoneNumber || '',
                    email: detailData.email || '',
                }));
                setIsCustomName(true);
                syncedFromDetailRef.current = true;
            }
            return;
        }

        if (justOpened) {
            const orgAny = organization as { logo?: string };
            const countryRef = organization.country;
            const countryTitle =
                countryRef == null
                    ? undefined
                    : typeof countryRef === 'string'
                      ? countryRef
                      : (countryRef as unknown as { title?: string }).title;
            const countryId =
                countryRef != null
                    ? apiCountries.find((c) => c.title === countryTitle)?.countryId ?? null
                    : null;
            const organizationTypeId = organization.type
                ? apiOrganizationTypes.find((ot) => ot.title === organization.type)?.organizationTypeId ?? null
                : null;
            const title = organization.name || '';
            setFormData((prev) => ({
                ...prev,
                title,
                logo: orgAny.logo || '',
                countryId,
                organizationTypeId,
                businessRegistrationNumber: organization.businessNumber || '',
                isPartner: toBooleanIsPartner((organization as { isPartner?: unknown }).isPartner),
            }));
            setIsCustomName(true);
        }
    }, [isOpen, organization, detailData, apiCountries, apiOrganizationTypes, apiCountryCallingCodes, isCreateMode]);

    // create 모드에서 국가 목록 로드 후 기본값 대한민국 및 전화 국가코드 적용
    useEffect(() => {
        if (!isOpen || !isCreateMode || apiCountries.length === 0) return;
        const krId = getKoreaCountry()?.countryId ?? null;
        if (krId == null) return;
        setFormData((prev) => {
            const countryId = prev.countryId ?? krId;
            const countryCallingCodeId = prev.countryCallingCodeId ?? getDefaultCallingCodeId(countryId);
            return { ...prev, countryId, countryCallingCodeId };
        });
    }, [isOpen, isCreateMode, apiCountries, apiCountryCallingCodes]);

    const debouncedSetSearchText = useCallback(
        (() => {
            let timeoutId: NodeJS.Timeout | null = null;
            return (value: string) => {
                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    setSearchText(value);
                }, 300);
            };
        })(),
        []
    );

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, title: value }));
        if (errors.title) setErrors((prev) => ({ ...prev, title: '' }));
        
        if (value.trim().length > 0) {
            debouncedSetSearchText(value);
            setShowSuggestions(true);
        } else {
            setSearchText('');
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion: string) => {
        setFormData((prev) => ({ ...prev, title: suggestion }));
        setShowSuggestions(false);
        setSearchText('');
    };

    const handleCustomNameToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsCustomName(e.target.checked);
        setShowSuggestions(false);
    };

    const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setAlertState({ isOpen: true, message: t('orgModal.errorImageOnly') });
            return;
        }
        setLogoUploading(true);
        try {
            const fileUrl = await uploadFileViaPresignedUrl(file, 'CREATOR');
            setFormData((prev) => ({ ...prev, logo: fileUrl }));
        } catch (err) {
            const message = err instanceof Error ? err.message : t('orgModal.errorLogoUpload');
            setAlertState({ isOpen: true, message });
        } finally {
            setLogoUploading(false);
            e.target.value = '';
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const target = e.target;
        const name = target.name;
        if (name === 'isPartner') {
            setFormData((prev) => ({ ...prev, isPartner: (target as HTMLInputElement).checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: target.value }));
        }
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.title.trim()) newErrors.title = t('orgModal.errorOrgNameRequired');
        if (!formData.countryId) newErrors.countryId = t('orgModal.errorCountryRequired');
        if (!formData.organizationTypeId) newErrors.organizationTypeId = t('orgModal.errorOrgTypeRequired');
        if (!formData.managerName.trim()) newErrors.managerName = t('orgModal.errorManagerRequired');
        if (formData.phoneNumber.trim() && !formData.countryCallingCodeId) newErrors.countryCallingCodeId = t('orgModal.errorPhoneCodeRequired');
        if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('orgModal.errorEmailInvalid');
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            const orgFieldKeys = ['title', 'countryId', 'organizationTypeId'] as const;
            if (orgFieldKeys.some((k) => Boolean(newErrors[k]))) {
                setOrgEditTab('organization');
            } else {
                setOrgEditTab('contact');
            }
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (isCreateMode) {
            const phoneNumber = formData.phoneNumber.trim();
            const email = formData.email.trim();
            const requestData: CreateOrganizationRequestDto = {
                title: formData.title.trim(),
                countryId: formData.countryId!,
                organizationTypeId: formData.organizationTypeId!,
                logoImageUrl: formData.logo.trim() || undefined,
                businessRegistrationNumber: formData.businessRegistrationNumber.trim() || undefined,
                isPartner: formData.isPartner,
                countryCallingCodeId: formData.countryCallingCodeId ?? countryCallingCodeOptions.find((ccc) => ccc.code === '+82')?.countryCallingCodeId ?? 1,
                managerName: formData.managerName.trim(),
                department: formData.department.trim() || undefined,
                phoneNumber: phoneNumber || undefined,
                email: email || undefined,
            };
            try {
                await createMutation.mutateAsync(requestData);
                setAlertState({ isOpen: true, message: t('orgModal.createSuccess') });
                onSave?.(null);
                setTimeout(() => onClose(), 1500);
            } catch (err) {
                const message = err instanceof Error ? err.message : t('orgModal.createFailed');
                setAlertState({ isOpen: true, message });
            }
            return;
        }

        if (!organization || !organizationId) return;
        const id = Number(organization.id);
        if (!id) {
            setAlertState({ isOpen: true, message: t('orgModal.orgIdInvalid') });
            return;
        }
        const current = detailData;
        if (!current?.country?.id || !current?.organizationType?.id) {
            setAlertState({ isOpen: true, message: t('orgModal.loadOrgThenSave') });
            return;
        }
        const phoneNumber = formData.phoneNumber.trim();
        const email = formData.email.trim();
        const requestData: UpdateOrganizationBodyDto = {
            title: formData.title.trim(),
            logoImageUrl: formData.logo.trim() || undefined,
            countryId: formData.countryId!,
            organizationTypeId: formData.organizationTypeId!,
            businessRegistrationNumber: formData.businessRegistrationNumber.trim() || undefined,
            isPartner: formData.isPartner,
            managerName: formData.managerName.trim(),
            department: formData.department.trim() || undefined,
            countryCallingCodeId: formData.countryCallingCodeId ?? countryCallingCodeOptions.find((ccc) => ccc.code === '+82')?.countryCallingCodeId ?? 1,
            phoneNumber: phoneNumber || undefined,
            email: email || undefined,
        };
        try {
            await updateMutation.mutateAsync({ organizationId: id, data: requestData });
            setAlertState({ isOpen: true, message: t('orgModal.updateSuccess') });
            onSave?.(organization);
            setTimeout(() => onClose(), 1500);
        } catch (err) {
            const message = err instanceof Error ? err.message : t('orgModal.updateFailed');
            setAlertState({ isOpen: true, message });
        }
    };

    const handleClose = () => {
        setErrors({});
        setShowSuggestions(false);
        onClose();
    };

    const formRowClassName = 'hover:bg-transparent border-b border-gray-200';
    const labelCellClassName = 'w-40 py-3 pr-4 text-sm font-medium text-gray-700 align-middle whitespace-nowrap bg-gray-50/80';
    const valueCellClassName = 'py-3 align-middle';

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={isCreateMode ? t('orgModal.createTitle') : t('orgModal.editTitle')}
                size="large"
                dialogClassName="h-[min(36rem,85vh)]"
                footer={
                    <>
                        <Button variant="outline" onClick={handleClose}>
                            {t('orgModal.cancel')}
                        </Button>
                        <Button
                            variant="lightdark"
                            onClick={handleSubmit}
                            disabled={updateMutation.isPending || createMutation.isPending || logoUploading}
                        >
                            {isCreateMode
                                ? (createMutation.isPending ? t('orgModal.processing') : t('orgModal.register'))
                                : (updateMutation.isPending ? t('orgModal.processing') : t('orgModal.save'))}
                        </Button>
                    </>
                }
            >
                <form onSubmit={handleSubmit}>
                    {!isCreateMode && isDetailLoading && !organization && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-700">{t('orgModal.loading')}</p>
                        </div>
                    )}
                    <Tabs
                        value={orgEditTab}
                        onValueChange={(v) => {
                            if (v === 'organization' || v === 'contact') setOrgEditTab(v);
                        }}
                        className="w-full"
                    >
                        <TabsList className="mb-4 grid h-auto w-full grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1">
                            <TabsTrigger value="organization" className="rounded-md py-2">
                                {t('orgModal.tabOrganizationInfo')}
                            </TabsTrigger>
                            <TabsTrigger value="contact" className="rounded-md py-2">
                                {t('orgModal.tabContactInfo')}
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="organization" className="mt-0 focus-visible:ring-0">
                            <Table>
                        <TableBody>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.orgName')} <span className="text-red-500">*</span>
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <div className="relative flex items-center">
                                        <Input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={isCustomName ? (e) => handleChange(e as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) : handleTitleChange}
                                            placeholder={t('orgModal.orgNamePlaceholder')}
                                            error={errors.title}
                                            wrapperClassName="mb-0 w-full"
                                        />
                                        {!isCustomName && showSuggestions && suggestedOrganizations.length > 0 && (
                                            <div className="absolute top-full left-0 z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                                                <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                                                    유사한 기관이 {suggestedOrganizations.length}개 있습니다
                                                </div>
                                                {suggestedOrganizations.map((org) => (
                                                    <div
                                                        key={org.id}
                                                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-900 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-2"
                                                        onClick={() => handleSuggestionClick(org.title)}
                                                    >
                                                        {org.logoImageUrl && (
                                                            <img 
                                                                src={org.logoImageUrl} 
                                                                alt="" 
                                                                className="w-6 h-6 object-contain rounded"
                                                            />
                                                        )}
                                                        <span className="flex-1">{org.title}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.country')} <span className="text-red-500">*</span>
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <SimpleSelect
                                        name="countryId"
                                        value={String(formData.countryId ?? '')}
                                        onChange={(e) => {
                                            const countryId = e.target.value ? Number(e.target.value) : null;
                                            const countryCallingCodeId = getDefaultCallingCodeId(countryId);
                                            setFormData((prev) => ({
                                                ...prev,
                                                countryId,
                                                countryCallingCodeId,
                                            }));
                                            if (errors.countryId) setErrors((prev) => ({ ...prev, countryId: '' }));
                                            if (errors.countryCallingCodeId) setErrors((prev) => ({ ...prev, countryCallingCodeId: '' }));
                                        }}
                                        error={errors.countryId}
                                        wrapperClassName="mb-0 w-full"
                                    >
                                        <option value="">{t('orgModal.select')}</option>
                                        {apiCountries.map((c) => (
                                            <option key={c.countryId} value={c.countryId}>
                                                {c.title}
                                            </option>
                                        ))}
                                    </SimpleSelect>
                                </TableCell>
                            </TableRow>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.orgType')} <span className="text-red-500">*</span>
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <SimpleSelect
                                        name="organizationTypeId"
                                        value={String(formData.organizationTypeId ?? '')}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                organizationTypeId: e.target.value ? Number(e.target.value) : null,
                                            }))
                                        }
                                        error={errors.organizationTypeId}
                                        wrapperClassName="mb-0 w-full"
                                    >
                                        <option value="">{t('orgModal.select')}</option>
                                        {apiOrganizationTypes.map((ot) => (
                                            <option key={ot.organizationTypeId} value={ot.organizationTypeId}>
                                                {ot.title}
                                            </option>
                                        ))}
                                    </SimpleSelect>
                                </TableCell>
                            </TableRow>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.businessNumber')}
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <Input
                                        type="text"
                                        name="businessRegistrationNumber"
                                        value={formData.businessRegistrationNumber}
                                        onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>)}
                                        placeholder={t('orgModal.businessNumberPlaceholder')}
                                        wrapperClassName="mb-0 w-full"
                                    />
                                </TableCell>
                            </TableRow>
                            {isAdmin && (
                                <TableRow className={formRowClassName}>
                                    <TableHead scope="row" className={labelCellClassName}>
                                        {t('orgModal.partnerType')}
                                    </TableHead>
                                    <TableCell className={valueCellClassName}>
                                        <RadioGroup
                                            value={formData.isPartner ? 'partner' : 'customer'}
                                            onValueChange={(value) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    isPartner: value === 'partner',
                                                }))
                                            }
                                            className="flex-row gap-6"
                                        >
                                            <RadioGroupItem value="customer">
                                                {t('orgModal.customer')}
                                            </RadioGroupItem>
                                            <RadioGroupItem value="partner">
                                                {t('orgModal.partner')}
                                            </RadioGroupItem>
                                        </RadioGroup>
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.logo')}
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <div className="flex items-center gap-3">
                                        {formData.logo && (
                                            <div className="group relative h-10 w-28 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                                                <img src={formData.logo} alt={t('orgModal.logoAlt')} className="h-full w-full object-contain" />
                                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                                                    <button
                                                        type="button"
                                                        disabled={logoUploading}
                                                        onClick={() => setFormData((prev) => ({ ...prev, logo: '' }))}
                                                        className="pointer-events-auto rounded-full bg-white p-1.5 text-red-600 shadow-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50"
                                                        title={t('orgModal.removeLogo')}
                                                        aria-label={t('orgModal.removeLogo')}
                                                    >
                                                        <Trash2 size={14} strokeWidth={2} aria-hidden />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <label className="cursor-pointer">
                                            <span className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                                                {logoUploading ? t('orgModal.uploading') : t('orgModal.selectImage')}
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={logoUploading}
                                                onChange={handleLogoFileChange}
                                            />
                                        </label>
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                            </Table>
                        </TabsContent>
                        <TabsContent value="contact" className="mt-0 focus-visible:ring-0">
                            <Table>
                        <TableBody>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.managerNameLabel')} <span className="text-red-500">*</span>
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <Input
                                        type="text"
                                        name="managerName"
                                        value={formData.managerName}
                                        onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>)}
                                        placeholder={t('orgModal.placeholderManager')}
                                        error={errors.managerName}
                                        wrapperClassName="mb-0"
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.departmentLabel')}
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <Input
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>)}
                                        placeholder={t('orgModal.placeholderDepartment')}
                                        wrapperClassName="mb-0"
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.phoneLabel')}
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <div className="flex gap-2 items-start">
                                        <SimpleSelect
                                            name="countryCallingCodeId"
                                            value={String(formData.countryCallingCodeId ?? '')}
                                            onChange={(e) => {
                                                const v = e.target.value ? Number(e.target.value) : null;
                                                setFormData((prev) => ({ ...prev, countryCallingCodeId: v }));
                                                if (errors.countryCallingCodeId)
                                                    setErrors((prev) => ({ ...prev, countryCallingCodeId: '' }));
                                            }}
                                            error={errors.countryCallingCodeId}
                                            wrapperClassName="mb-0 w-24 shrink-0"
                                        >
                                            <option value="">{t('orgModal.select')}</option>
                                            {countryCallingCodeOptions.map((ccc) => (
                                                <option key={ccc.countryCallingCodeId} value={ccc.countryCallingCodeId}>
                                                    {ccc.code}
                                                </option>
                                            ))}
                                        </SimpleSelect>
                                        <Input
                                            type="tel"
                                            name="phoneNumber"
                                            value={formData.phoneNumber}
                                            onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>)}
                                            placeholder={t('orgModal.placeholderPhone')}
                                            error={errors.phoneNumber}
                                            wrapperClassName="mb-0 flex-1 min-w-0"
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow className={formRowClassName}>
                                <TableHead scope="row" className={labelCellClassName}>
                                    {t('orgModal.emailLabel')}
                                </TableHead>
                                <TableCell className={valueCellClassName}>
                                    <Input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>)}
                                        placeholder={t('orgModal.placeholderEmail')}
                                        error={errors.email}
                                        wrapperClassName="mb-0"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                            </Table>
                        </TabsContent>
                    </Tabs>
                </form>
            </Modal>
            <AlertDialog
                isOpen={alertState.isOpen}
                onClose={() => setAlertState({ isOpen: false, message: '' })}
                message={alertState.message}
            />
        </>
    );
}
